import { useState } from 'react'
import PasswordRequirements from '../components/auth/PasswordRequirements'
import MetaTags from '../components/common/MetaTags'
import { useAuth } from '../hooks/useAuth'
import { authService } from '../services/authService'

export default function AccountPage() {
  const { user, logout } = useAuth()

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [passwordSuccess, setPasswordSuccess] = useState('')
  const [isChangingPassword, setIsChangingPassword] = useState(false)

  // Delete account state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setPasswordError('')
    setPasswordSuccess('')

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match')
      return
    }

    if (newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters')
      return
    }

    setIsChangingPassword(true)
    try {
      await authService.changePassword(currentPassword, newPassword, confirmPassword)
      setPasswordSuccess('Password changed successfully!')
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    } catch (err: unknown) {
      const error = err as { response?: { data?: { errors?: { full_messages?: string[] } } } }
      setPasswordError(
        error.response?.data?.errors?.full_messages?.join(', ') || 'Failed to change password'
      )
    } finally {
      setIsChangingPassword(false)
    }
  }

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') return

    setIsDeleting(true)
    try {
      await authService.deleteAccount()
      await logout()
      window.location.href = '/'
    } catch (err) {
      console.error('Failed to delete account:', err)
      setIsDeleting(false)
    }
  }

  return (
    <div className="max-w-2xl mx-auto">
      <MetaTags
        title="Account Settings | profile.work"
        description="Manage your account settings, change password, and more."
      />

      {/* Header */}
      <div className="mb-8">
        <h1
          className="text-2xl sm:text-3xl text-black mb-2"
          style={{ fontFamily: "'Instrument Serif', serif" }}
        >
          Account Settings
        </h1>
        <p className="text-black/60">Manage your account security and preferences.</p>
      </div>

      {/* Account Information */}
      <div className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-xl border border-black/10 p-6 mb-6">
        <h2 className="text-lg font-semibold text-black mb-4">Account Information</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-black/60 mb-1">Email</label>
            <p className="text-black">{user?.email}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-black/60 mb-1">Sign-in Method</label>
            <p className="text-black flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              Email & Password
            </p>
          </div>
        </div>
      </div>

      {/* Change Password */}
      <div className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-xl border border-black/10 p-6 mb-6">
        <h2 className="text-lg font-semibold text-black mb-4">Change Password</h2>

        {passwordError && (
          <div className="mb-4 p-3 rounded-lg bg-red-100 border border-red-300">
            <p className="text-xs text-red-800 flex items-center gap-1.5">
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              {passwordError}
            </p>
          </div>
        )}

        {passwordSuccess && (
          <div className="mb-4 p-3 rounded-lg bg-black/5 border border-black/10">
            <p className="text-xs text-black flex items-center gap-1.5">
              <svg
                className="w-3.5 h-3.5 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {passwordSuccess}
            </p>
          </div>
        )}

        <form onSubmit={handleChangePassword} className="space-y-4">
          <div>
            <label
              htmlFor="current-password"
              className="block text-sm font-medium text-black/70 mb-1"
            >
              Current Password
            </label>
            <input
              id="current-password"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              className="block w-full px-3 py-2.5 border border-black/20 rounded-xl text-black placeholder-black/40 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-all bg-white/50 text-sm"
              placeholder="Enter current password"
            />
          </div>
          <div>
            <label htmlFor="new-password" className="block text-sm font-medium text-black/70 mb-1">
              New Password
            </label>
            <input
              id="new-password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={6}
              className="block w-full px-3 py-2.5 border border-black/20 rounded-xl text-black placeholder-black/40 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-all bg-white/50 text-sm"
              placeholder="Min. 6 characters"
            />
          </div>
          <div>
            <label
              htmlFor="confirm-password"
              className="block text-sm font-medium text-black/70 mb-1"
            >
              Confirm New Password
            </label>
            <input
              id="confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="block w-full px-3 py-2.5 border border-black/20 rounded-xl text-black placeholder-black/40 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-all bg-white/50 text-sm"
              placeholder="Re-enter new password"
            />
            <PasswordRequirements
              password={newPassword}
              passwordConfirmation={confirmPassword}
              showConfirmation={true}
            />
          </div>
          <button
            type="submit"
            disabled={isChangingPassword || !currentPassword || !newPassword || !confirmPassword}
            className="w-full py-2.5 px-4 bg-gray-800 text-white font-semibold rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm"
          >
            {isChangingPassword ? 'Changing Password...' : 'Change Password'}
          </button>
        </form>
      </div>

      {/* Sign Out */}
      <div className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-xl border border-black/10 p-6 mb-6">
        <h2 className="text-lg font-semibold text-black mb-4">Sign Out</h2>
        <p className="text-black/60 text-sm mb-4">Sign out of your account on this device.</p>
        <button
          onClick={logout}
          className="py-2.5 px-4 bg-white/50 border border-black/20 text-black font-medium rounded-lg hover:bg-black/5 transition-all text-sm"
        >
          Sign Out
        </button>
      </div>

      {/* Danger Zone */}
      <div className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-xl border border-red-200 p-6">
        <h2 className="text-lg font-semibold text-red-600 mb-4">Danger Zone</h2>
        <p className="text-black/60 text-sm mb-4">
          Once you delete your account, there is no going back. This will permanently delete your
          account and all associated data including your profile.
        </p>
        <button
          onClick={() => setShowDeleteConfirm(true)}
          className="py-2.5 px-4 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-all text-sm"
        >
          Delete Account
        </button>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-md"
            onClick={() => setShowDeleteConfirm(false)}
          />
          <div
            className="relative bg-white/70 backdrop-blur-xl rounded-2xl shadow-xl w-full max-w-md mx-4 p-6"
            style={{ animation: 'fadeInScale 0.2s ease-out' }}
          >
            <h3 className="text-lg font-semibold text-black mb-2">Delete Account?</h3>
            <p className="text-black/60 mb-4">
              This will permanently delete your account and all associated data. This action cannot
              be undone.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-black/70 mb-1">
                Type <span className="font-bold">DELETE</span> to confirm
              </label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                className="block w-full px-3 py-2.5 border border-black/20 rounded-xl text-black placeholder-black/40 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-all bg-white/50 text-sm"
                placeholder="DELETE"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false)
                  setDeleteConfirmText('')
                }}
                className="flex-1 py-2 px-4 bg-black/10 text-black font-medium rounded-lg hover:bg-black/20"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={isDeleting || deleteConfirmText !== 'DELETE'}
                className="flex-1 py-2 px-4 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {isDeleting ? 'Deleting...' : 'Delete Account'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Keyframe animation */}
      <style>{`
        @keyframes fadeInScale {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  )
}
